import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

import world from '../../assets/world.svg';
import check from '../../assets/check-onyx.svg';
import chevron from '../../assets/chevron-white.svg';

import { FILE_CLAIMBOT, WELCOME_DEMOGRAPHICS } from './routes.constants';
import { useComboBoxLanguage } from '../../hooks/useFeatureFlags';
import { getLanguages, saveLanguagePreference } from '../../actions';
import i18n from '../../i18n';
import { doNothing } from '../../services/utils';

import './LanguageComboBox.css';

const IGNORE_PATH = [FILE_CLAIMBOT, WELCOME_DEMOGRAPHICS];

const LanguageComboBox = () => {
  const store = useSelector(({ common }) => ({ common }), shallowEqual);
  const { common: { languages } } = store;
  const dispatch = useDispatch();
  const location = useLocation();
  const { t } = useTranslation('common');
  const [languageHighlited, setLanguageHighlited] = useState({});
  const [open, setOpen] = useState(false);
  const useComboBoxFeatureFlag = useComboBoxLanguage();

  const ignoreRouteToReload = React.useMemo(
    () => IGNORE_PATH.includes(location.pathname), [location.pathname],
  );

  const userLanguages = useMemo(() => {
    if (languages && languages.data && languages.data.length) {
      return languages.data;
    }

    return [];
  }, [languages]);

  const updateOsanoLocale = (language) => {
    const [locale] = language.split('-');

    try {
      if (window?.Osano?.cm) {
        window.Osano.cm.locale = locale;
      }
    } catch (_) {
      doNothing();
    }
  };

  useEffect(() => {
    if (useComboBoxFeatureFlag) {
      dispatch(getLanguages());

      updateOsanoLocale(i18n.language);
    }
  }, []);

  const getLanguageLabelByCode = useCallback((languageKey) => {
    const language = userLanguages
      .find((lang) => lang.code === languageKey)?.displayName;
    return language || languageKey;
  }, [userLanguages]);

  const onLanguageClick = (item) => () => {
    updateOsanoLocale(item.code);

    dispatch(saveLanguagePreference({
      preferredLanguage: item.code,
    }));
    i18n.changeLanguage(item.code);

    if (!ignoreRouteToReload) {
      window.location.reload();
    }
  };

  const onLanguageKeyPress = (item) => (event) => {
    const { key } = event;
    if (key === 'Enter' || key === ' ') {
      onLanguageClick(item)();
    }
  };

  const onComboBoxClick = () => {
    setOpen((prev) => !prev);
  };

  const onArrowKeyPressed = (event) => {
    const { keyCode } = event;
    const ENTER_KEY = 13;
    const ESC_KEY = 27;
    const TAB_KEY = 9;
    const UP_KEY = 38;
    const DOWN_KEY = 40;
    const SPACE_KEY = 32;

    switch (keyCode) {
      case UP_KEY:
      case DOWN_KEY: {
        event.preventDefault();

        const factor = keyCode === UP_KEY ? -1 : 1;
        const temp = userLanguages
          .findIndex((language) => language.code === languageHighlited.code);

        if (userLanguages[temp + factor]) {
          const highlited = userLanguages[temp + factor];
          setLanguageHighlited(highlited);
        }

        break;
      }

      case SPACE_KEY:
      case ENTER_KEY: {
        event.preventDefault();

        // open with selection
        if (open && languageHighlited.code) {
          const language = userLanguages
            .find((element) => element.code === languageHighlited.code);

          if (language) {
            onLanguageClick(language)();
            setOpen(false);
          }
        } else if (open) {
          // open without selection
          setOpen(false);
        } else {
          // closed
          setOpen(true);
        }

        break;
      }

      case TAB_KEY: {
        // no stop propagation so the user can easyly navigate
        // through the page with tab key
        if (open) {
          setOpen(false);
        }

        break;
      }

      case ESC_KEY: {
        setOpen(false);
        break;
      }

      default: {
        break;
      }
    }
  };

  const renderItems = useCallback(() => {
    if (!open) {
      return null;
    }

    return (
      <ul
        aria-labelledby="laguageComboBoxId"
        aria-live="off"
        className="Language-combo-box-list"
        role="listbox"
      >
        {userLanguages.map((language) => (
          <li
            key={language.code}
            aria-selected={language.code === i18n.language}
            aria-setsize={userLanguages.length}
            data-highlighted={language.code === languageHighlited.code}
            id={`dropdownItem${language.code}`}
            onClick={onLanguageClick(language)}
            onKeyDown={onArrowKeyPressed}
            onKeyPress={onLanguageKeyPress(language)}
            role="option"
          >
            <span>
              {language.displayName}
            </span>

            {language.code === i18n.language
              ? <img alt="" src={check} /> : null}
          </li>
        ))}
      </ul>
    );
  }, [userLanguages, open, languageHighlited]);

  if (!useComboBoxFeatureFlag) {
    return null;
  }

  const renderContent = useCallback(() => {
    if (languages.loading) {
      return <span>{t('languageComboBox.loading')}</span>;
    }

    return (
      <>
        <img alt="" src={world} />

        <label
          aria-label={t('languageComboBox.languageSelector')}
          htmlFor="laguageComboBoxId"
          id="labelLaguange"
        >
          {getLanguageLabelByCode(i18n.language)}

          <img
            alt=""
            className="Language-combo-box-chevron"
            data-open={open}
            src={chevron}
          />
        </label>

        {renderItems()}
      </>
    );
  }, [languages, open, t, languageHighlited]);

  return (
    <button
      className="Language-combo-box-container"
      onClick={onComboBoxClick}
      onKeyDown={onArrowKeyPressed}
      type="button"
    >
      {renderContent()}
    </button>
  );
};

export { LanguageComboBox };
